const TONE_SAMPLE_RATE = 16000;
const CALL_TONE_VARIANT_STORAGE_KEY = 'nettgrav.messengerV2.callToneVariant';
const DEFAULT_CALL_TONE_VARIANT = 'remembrance';
const CALL_TONE_VARIANTS = new Set([DEFAULT_CALL_TONE_VARIANT, 'warm']);

const toneDataUrls = new Map();

const encodeBase64 = (binary) => {
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    return window.btoa(binary);
  }
  return '';
};

const writeString = (view, offset, value) => {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
};

const canUseBrowserAudio = () =>
  typeof window !== 'undefined' &&
  typeof window.Audio === 'function' &&
  !/jsdom/i.test(String(window.navigator?.userAgent || ''));

const resolveCallToneVariant = () => {
  if (typeof window === 'undefined') return DEFAULT_CALL_TONE_VARIANT;
  try {
    const value = String(window.localStorage?.getItem(CALL_TONE_VARIANT_STORAGE_KEY) || '')
      .trim()
      .toLowerCase();
    return CALL_TONE_VARIANTS.has(value) ? value : DEFAULT_CALL_TONE_VARIANT;
  } catch {
    return DEFAULT_CALL_TONE_VARIANT;
  }
};

const createToneDataUrl = ({ key, durationSeconds, sampleAt }) => {
  if (toneDataUrls.has(key)) return toneDataUrls.get(key);

  const samples = Math.floor(TONE_SAMPLE_RATE * durationSeconds);
  const bytesPerSample = 2;
  const dataSize = samples * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, TONE_SAMPLE_RATE, true);
  view.setUint32(28, TONE_SAMPLE_RATE * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  for (let index = 0; index < samples; index += 1) {
    const time = index / TONE_SAMPLE_RATE;
    const sample = Math.max(-1, Math.min(1, Number(sampleAt(time)) || 0));
    view.setInt16(44 + index * bytesPerSample, sample * 0x7fff, true);
  }

  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }

  const url = `data:audio/wav;base64,${encodeBase64(binary)}`;
  toneDataUrls.set(key, url);
  return url;
};

const envelope = (time, start, end, attack = 0.02, release = 0.04) => {
  if (time < start || time > end) return 0;
  if (time - start < attack) return (time - start) / attack;
  if (end - time < release) return Math.max(0, (end - time) / release);
  return 1;
};

const dualTone = (time, firstFrequency, secondFrequency) =>
  (Math.sin(2 * Math.PI * firstFrequency * time) + Math.sin(2 * Math.PI * secondFrequency * time)) /
  2;

const bellTone = (time, start, duration, frequency, gain = 0.18) => {
  if (time < start || time > start + duration) return 0;
  const localTime = time - start;
  const decay = Math.exp(-localTime * 2.2);
  const noteEnvelope = envelope(time, start, start + duration, 0.035, 0.28);
  const fundamental = Math.sin(2 * Math.PI * frequency * localTime);
  const octave = Math.sin(2 * Math.PI * frequency * 2 * localTime) * 0.35;
  const shimmer = Math.sin(2 * Math.PI * frequency * 3.01 * localTime) * 0.16;
  return (fundamental + octave + shimmer) * gain * decay * noteEnvelope;
};

const createRemembranceRingtoneUrl = () =>
  createToneDataUrl({
    key: 'incoming-remembrance',
    durationSeconds: 3.4,
    sampleAt: (time) =>
      bellTone(time, 0, 1.35, 523.25, 0.2) +
      bellTone(time, 0.42, 1.28, 659.25, 0.16) +
      bellTone(time, 0.84, 1.36, 783.99, 0.14) +
      bellTone(time, 1.9, 1.2, 659.25, 0.14),
  });

const createWarmRingtoneUrl = () =>
  createToneDataUrl({
    key: 'incoming-warm',
    durationSeconds: 2.8,
    sampleAt: (time) =>
      bellTone(time, 0, 1, 440, 0.16) +
      bellTone(time, 0.28, 1, 554.37, 0.14) +
      bellTone(time, 0.56, 1.1, 659.25, 0.14) +
      bellTone(time, 1.35, 1.05, 493.88, 0.15) +
      bellTone(time, 1.68, 1.05, 659.25, 0.13),
  });

const createIncomingRingtoneUrl = (variant = DEFAULT_CALL_TONE_VARIANT) => {
  if (variant === 'warm') return createWarmRingtoneUrl();
  return createRemembranceRingtoneUrl();
};

const createOutgoingCallingToneUrl = () =>
  createToneDataUrl({
    key: 'outgoing-calling',
    durationSeconds: 1.35,
    sampleAt: (time) => dualTone(time, 425, 450) * 0.22 * envelope(time, 0, 0.24),
  });

const createOutgoingRingingToneUrl = () =>
  createToneDataUrl({
    key: 'outgoing-ringing',
    durationSeconds: 3,
    sampleAt: (time) => {
      const firstBurst = envelope(time, 0, 0.42);
      const secondBurst = envelope(time, 0.62, 1.04);
      return dualTone(time, 440, 480) * 0.2 * Math.max(firstBurst, secondBurst);
    },
  });

const createCallEndedToneUrl = () =>
  createToneDataUrl({
    key: 'call-ended',
    durationSeconds: 0.42,
    sampleAt: (time) => {
      const pitch = time < 0.18 ? 520 : 390;
      return Math.sin(2 * Math.PI * pitch * time) * 0.2 * envelope(time, 0, 0.38, 0.01, 0.08);
    },
  });

const startLoopedTone = (url, volume = 0.45) => {
  if (!canUseBrowserAudio()) {
    return { stop: () => {} };
  }

  let audio = null;
  try {
    audio = new window.Audio(url);
    audio.loop = true;
    audio.volume = volume;
    const playResult = audio.play();
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => null);
    }
  } catch {
    audio = null;
  }

  return {
    stop: () => {
      try {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      } catch {}
    },
  };
};

export const startMessengerV2OutgoingCallTone = () =>
  startLoopedTone(createOutgoingCallingToneUrl(), 0.36);

export const startMessengerV2OutgoingRingingTone = () =>
  startLoopedTone(createOutgoingRingingToneUrl(), 0.42);

export const playMessengerV2CallEndedTone = () => {
  if (!canUseBrowserAudio()) return;
  try {
    const audio = new window.Audio(createCallEndedToneUrl());
    audio.volume = 0.42;
    const playResult = audio.play();
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => null);
    }
  } catch {}
};

export const startMessengerV2IncomingCallRingtone = () => {
  if (!canUseBrowserAudio()) {
    return { stop: () => {} };
  }

  let audio = null;
  let vibrationTimer = null;
  let stopped = false;
  const toneVariant = resolveCallToneVariant();

  const stopAudio = (target) => {
    try {
      target?.pause?.();
      if (target) target.currentTime = 0;
    } catch {}
  };

  try {
    audio = new window.Audio(createIncomingRingtoneUrl(toneVariant));
    audio.loop = true;
    audio.volume = 0.56;
    const playResult = audio.play();
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => {
        if (stopped) return;
        try {
          const fallbackAudio = new window.Audio(createIncomingRingtoneUrl(toneVariant));
          fallbackAudio.loop = true;
          fallbackAudio.volume = 0.55;
          if (stopped) {
            stopAudio(fallbackAudio);
            return;
          }
          audio = fallbackAudio;
          void fallbackAudio.play().catch(() => null);
        } catch {
          audio = null;
        }
      });
    }
  } catch {
    try {
      audio = new window.Audio(createIncomingRingtoneUrl(toneVariant));
      audio.loop = true;
      audio.volume = 0.55;
      const fallbackPlayResult = audio.play();
      if (fallbackPlayResult && typeof fallbackPlayResult.catch === 'function') {
        fallbackPlayResult.catch(() => null);
      }
    } catch {
      audio = null;
    }
  }

  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      navigator.vibrate([350, 180, 350]);
      vibrationTimer = window.setInterval(() => {
        try {
          navigator.vibrate([350, 180, 350]);
        } catch {}
      }, 1800);
    } catch {}
  }

  return {
    stop: () => {
      stopped = true;
      if (vibrationTimer) {
        window.clearInterval(vibrationTimer);
        vibrationTimer = null;
      }
      try {
        navigator?.vibrate?.(0);
      } catch {}
      stopAudio(audio);
      audio = null;
    },
  };
};

const previewIncomingCallTone = (variant = DEFAULT_CALL_TONE_VARIANT) => {
  const normalizedVariant = CALL_TONE_VARIANTS.has(String(variant || '').toLowerCase())
    ? String(variant || '').toLowerCase()
    : DEFAULT_CALL_TONE_VARIANT;
  if (!canUseBrowserAudio()) {
    return { stop: () => {} };
  }

  let audio = null;
  let autoStopTimer = null;
  try {
    audio = new window.Audio(createIncomingRingtoneUrl(normalizedVariant));
    audio.loop = true;
    audio.volume = 0.56;
    const playResult = audio.play();
    if (playResult && typeof playResult.catch === 'function') {
      playResult.catch(() => null);
    }
    autoStopTimer = window.setTimeout(() => {
      try {
        audio?.pause?.();
      } catch {}
    }, 8000);
  } catch {
    audio = null;
  }

  return {
    stop: () => {
      if (autoStopTimer) {
        window.clearTimeout(autoStopTimer);
      }
      try {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      } catch {}
    },
  };
};

if (typeof window !== 'undefined') {
  window.nettgravCallTones = {
    storageKey: CALL_TONE_VARIANT_STORAGE_KEY,
    variants: Array.from(CALL_TONE_VARIANTS),
    current: () => resolveCallToneVariant(),
    use: (variant = DEFAULT_CALL_TONE_VARIANT) => {
      const normalizedVariant = CALL_TONE_VARIANTS.has(String(variant || '').toLowerCase())
        ? String(variant || '').toLowerCase()
        : DEFAULT_CALL_TONE_VARIANT;
      try {
        window.localStorage?.setItem(CALL_TONE_VARIANT_STORAGE_KEY, normalizedVariant);
      } catch {}
      return normalizedVariant;
    },
    preview: previewIncomingCallTone,
  };
}
